import { Component } from '@theme/component';

/**
 * ALE fly-to-cart animation.
 * Keeps the selected-color quatrefoil intact for the entire flight and uses a sampled
 * quadratic curve to avoid the mid-path hitch caused by transform keyframe handoffs.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  /** @type {string} */
  aleColor = '';

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#confirmArrival();
      this.remove();
      return;
    }

    requestAnimationFrame(() => this.#animate());
  }

  #confirmArrival() {
    if (!(this.destination instanceof HTMLElement)) return;
    this.destination.dispatchEvent(
      new CustomEvent('ale:cart-color-pulse', {
        bubbles: false,
        detail: { color: this.aleColor || '' },
      })
    );
  }

  #animate = async () => {
    if (!(this.source instanceof Element) || !(this.destination instanceof Element)) {
      this.remove();
      return;
    }

    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const start = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const end = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.hypot(dx, dy);
    const lift = Math.min(150, Math.max(52, distance * 0.14));
    const control = {
      x: start.x + dx * 0.58,
      y: Math.min(start.y, end.y) - lift,
    };

    const frames = [];
    const steps = 30;
    for (let index = 0; index <= steps; index += 1) {
      const t = index / steps;
      const oneMinus = 1 - t;
      const x = oneMinus * oneMinus * start.x + 2 * oneMinus * t * control.x + t * t * end.x;
      const y = oneMinus * oneMinus * start.y + 2 * oneMinus * t * control.y + t * t * end.y;
      const scale = 1 - (0.68 * (t ** 1.35));
      const opacity = t < 0.84 ? 1 : 1 - ((t - 0.84) / 0.16) * 0.16;
      frames.push({
        transform: `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale})`,
        opacity,
        offset: t,
      });
    }

    const duration = this.classList.contains('fly-to-cart--quick')
      ? 620
      : this.classList.contains('fly-to-cart--sticky')
        ? 760
        : 790;

    const animation = this.animate(frames, {
      duration,
      easing: 'linear',
      fill: 'forwards',
    });

    try {
      await animation.finished;
      this.#confirmArrival();
    } catch (_) {
      // If the animation is cancelled (navigation/section reload), do not play arrival effects.
    }
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
